import type { AnalysisNote, AnalysisRun } from "@/lib/music/analyzer-types";
import type { MusicalTimeline } from "@/lib/music/musical-timeline";

/**
 * Les prises d'un récital enregistré.
 *
 * # Ce qu'on cherche
 *
 * Quelqu'un qui s'enregistre seul recommence. Il joue, se trompe, reprend la
 * chanson du début, et recommence jusqu'à la version qu'il gardera — sans rien
 * toucher, les mains sur le clavier. L'enregistrement contient donc cinq
 * essais du même morceau, à la suite, et c'est à la relecture qu'il faut les
 * retrouver : on ne demande aucun geste à quelqu'un qui joue.
 *
 * # Le signal : le début rejoué, précédé d'un trou
 *
 * Reprendre, c'est rejouer les premières notes. On garde donc la **signature**
 * du début — les premiers accords, en hauteurs, sans le rythme — et on la
 * cherche plus loin.
 *
 * Mais la signature seule se trompe : un refrain qui reprend le motif
 * d'ouverture, une reprise *da capo*, et la chanson se découperait en
 * morceaux. Ce qui distingue les deux, c'est **le trou** : quand on reprend
 * après une faute, on s'arrête, on souffle, on remet les mains ; quand le motif
 * revient dans la chanson, il arrive en mesure. La signature ne coupe donc
 * qu'après un silence, et un silence franc coupe de lui-même.
 *
 * # Ce que ça ne sait pas faire
 *
 * Reconnaître une reprise qui ne repart **pas** du début : rien ne la distingue
 * de la suite du morceau. Il y faudrait un geste — la pédale tenue, par
 * exemple, puisque les mains restent sur les touches.
 *
 * Et rien de tout cela ne coupe le fichier : une prise n'est qu'une fenêtre,
 * que le lecteur montre et que la sélection du sélecteur de structure peut
 * élargir jusqu'à l'enregistrement entier.
 */

/** Les notes jouées ensemble : en dessous, c'est un accord, pas deux frappes. */
const CHORD_TICKS_RATIO = 1 / 8;

/** Combien de frappes forment la signature du début. */
const SIGNATURE_ONSETS = 6;

/** Ce qu'on tolère de fausses notes dans une signature : on se trompe, justement. */
const SIGNATURE_MISSES = 1;

/**
 * Le trou minimal avant une reprise, en secondes.
 *
 * Deux secondes et demie, c'est très long au milieu d'une chanson — même un
 * point d'orgue ou une respiration de phrase n'y arrive pas — et très court
 * pour quelqu'un qui vient de se tromper et qui recommence.
 */
const RESTART_GAP_SECONDS = 2.5;

/**
 * Le silence qui coupe tout seul, en secondes : au-delà, quoi qu'il se joue
 * ensuite, ce n'est plus la même traversée.
 */
const LONG_SILENCE_SECONDS = 15;

/** En dessous, ce n'est pas une prise mais un faux départ : il rejoint la suivante. */
const MIN_TAKE_ONSETS = 4;

/** Une frappe : les hauteurs jouées ensemble, et ce qu'elles occupent. */
interface Onset {
  tick: number;
  endTick: number;
  /** Les hauteurs, rangées : deux accords identiques se comparent à l'identique. */
  pitches: number[];
  notes: number;
}

/**
 * Découpe un enregistrement en prises. Rend une liste vide quand il n'y en a
 * qu'une : il n'y a alors rien à choisir, et une entrée unique dans un
 * sélecteur ne dit rien de plus que le morceau lui-même.
 */
export function findTakes(
  notes: readonly AnalysisNote[],
  timeline: MusicalTimeline,
  totalTicks: number,
): AnalysisRun[] {
  const onsets = groupOnsets(notes, timeline.getPpq() * CHORD_TICKS_RATIO);

  if (onsets.length <= MIN_TAKE_ONSETS) {
    return [];
  }

  const signature = onsets.slice(0, SIGNATURE_ONSETS).map((onset) => onset.pitches);
  const starts = [0];

  for (let index = 1; index < onsets.length; index += 1) {
    const gap = timeline.tickToSeconds(onsets[index].tick) - timeline.tickToSeconds(onsets[index - 1].endTick);

    if (gap < RESTART_GAP_SECONDS) {
      continue;
    }

    if (gap >= LONG_SILENCE_SECONDS || matchesSignature(onsets, index, signature)) {
      starts.push(index);
    }
  }

  return runsOf(onsets, merged(onsets, starts), timeline, totalTicks);
}

/** Les notes qui tombent ensemble, en une frappe. */
function groupOnsets(notes: readonly AnalysisNote[], window: number): Onset[] {
  const onsets: Onset[] = [];

  for (const note of notes) {
    const last = onsets[onsets.length - 1];

    if (last !== undefined && note.tick - last.tick <= window) {
      last.pitches.push(note.midi);
      last.endTick = Math.max(last.endTick, note.tick + note.durationTicks);
      last.notes += 1;

      continue;
    }

    onsets.push({
      tick: note.tick,
      endTick: note.tick + note.durationTicks,
      pitches: [note.midi],
      notes: 1,
    });
  }

  for (const onset of onsets) {
    onset.pitches.sort((a, b) => a - b);
  }

  return onsets;
}

/** Ce qui commence ici rejoue-t-il le début ? */
function matchesSignature(onsets: readonly Onset[], from: number, signature: readonly number[][]): boolean {
  if (from + signature.length > onsets.length) {
    return false;
  }

  let missed = 0;

  for (let step = 0; step < signature.length; step += 1) {
    if (!samePitches(onsets[from + step].pitches, signature[step])) {
      missed += 1;

      if (missed > SIGNATURE_MISSES) {
        return false;
      }
    }
  }

  return true;
}

function samePitches(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((pitch, index) => pitch === b[index]);
}

/**
 * Absorbe les faux départs : trois notes puis un blanc ne sont pas une prise,
 * et les lister ferait une liste que personne ne lit. Leurs notes restent où
 * elles sont — rien ne se perd, la prise suivante commence juste avant elles.
 */
function merged(onsets: readonly Onset[], starts: readonly number[]): number[] {
  const kept: number[] = [];

  for (let index = 0; index < starts.length; index += 1) {
    const from = starts[index];
    const to = starts[index + 1] ?? onsets.length;

    if (to - from >= MIN_TAKE_ONSETS || kept.length === 0) {
      kept.push(from);

      continue;
    }

    // Un faux départ à la toute fin n'a personne pour l'absorber : il reste
    // avec la prise qui le précède.
  }

  return kept;
}

function runsOf(
  onsets: readonly Onset[],
  starts: readonly number[],
  timeline: MusicalTimeline,
  totalTicks: number,
): AnalysisRun[] {
  if (starts.length < 2) {
    return [];
  }

  return starts.map((start, index) => {
    const next = starts[index + 1];
    const last = next === undefined ? onsets.length - 1 : next - 1;
    const startTick = onsets[start].tick;
    // Jusqu'au début de la suivante : ce qui est entre deux prises — le temps
    // de souffler — appartient à celle qu'on vient de jouer, plutôt qu'à
    // personne. Une sélection reste ainsi continue d'un bout à l'autre.
    const endTick = next === undefined ? totalTicks : onsets[next].tick - 1;

    // Les mesures, elles, ne se chevauchent jamais : une mesure appartient à
    // la prise qui l'ouvre. Le sélecteur de structure raisonne en mesures, et
    // deux prises qui s'en disputeraient une éclaireraient deux lignes à la
    // fois. Les ticks, eux, restent exacts — c'est eux qu'une sélection
    // publiera.
    const startMeasure = timeline.getBarAtTick(startTick) + 1;
    const endMeasure = next === undefined
      ? Math.max(startMeasure, timeline.getBarAtTick(Math.max(startTick, endTick)) + 1)
      : Math.max(startMeasure, timeline.getBarAtTick(onsets[next].tick));

    return {
      id: `take-${index + 1}`,
      label: `Prise ${index + 1}`,
      kind: "take" as const,
      startTick,
      endTick,
      startMeasure,
      endMeasure,
      measures: endMeasure - startMeasure + 1,
      noteCount: onsets.slice(start, last + 1).reduce((total, onset) => total + onset.notes, 0),
    };
  });
}
